import { FlagPriority } from '@generated/graphql';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import { Tab, Tabs } from '@mui/material';
import type { Tokens } from '@ovComponents/0-tokens/useTokens';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Button } from '@ovComponents/2-component/button/button';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  badgeSeverityFromPriority,
  getReconFlagPriorityBadgeSx,
  severityFilterAccentColor,
} from '../utils/reconFlags.config';
import type { ReconFlagSeverity } from '../utils/reconFlags.types';
import { OverviewRow } from './overviewRow';
import {
  buildReconFlagDrawerHeader,
  buildReconFlagOverviewSections,
} from './reconFlagDrawerOverviewModel';
import { getReconFlagsDetailDrawerTokens } from './reconFlagsDetailDrawer.tokens';
import type {
  DetailTab,
  ReconFlagsDetailDrawerBodyProps,
} from './reconFlagsDetailDrawerBody.types';
import { ReconFlagsDrawerNotes } from './reconFlagsDrawerNotes';
import { ReconFlagsDrawerNotesHistory } from './reconFlagsDrawerNotesHistory';

const prioritySwatchColor = (
  sys: Tokens['sys'],
  priority: FlagPriority | null | undefined,
): string => {
  const outline = String(
    sys.color.outlineVariant ?? sys.color.outline ?? '#c4c4c4',
  );
  if (priority == null || priority === FlagPriority.Unknown) {
    return outline;
  }
  return severityFilterAccentColor(sys, priority as ReconFlagSeverity);
};

export const ReconFlagsDetailDrawerBody = ({
  sys,
  flag,
  t,
  notesDraft,
  onNotesDraftChange,
  onNotesSaved,
  notesDisabled = false,
}: ReconFlagsDetailDrawerBodyProps) => {
  const [tab, setTab] = useState<DetailTab>('overview');

  const tokens = useMemo(() => getReconFlagsDetailDrawerTokens(sys), [sys]);

  const header = useMemo(() => buildReconFlagDrawerHeader(flag), [flag]);

  const overviewSections = useMemo(
    () => buildReconFlagOverviewSections(t, flag),
    [flag, t],
  );

  const priorityBadge = useMemo(() => {
    if (!flag.priority || flag.priority === FlagPriority.Unknown) {
      return null;
    }
    return (
      <Badge
        label={t(`reconFlags.severityLabel.${flag.priority}`)}
        variant='text'
        sx={getReconFlagPriorityBadgeSx(
          sys,
          badgeSeverityFromPriority(flag.priority),
        )}
      />
    );
  }, [flag, sys, t]);

  const swatchColor = prioritySwatchColor(sys, flag.priority);

  const clientProfileLink = header.clientProfileUserId != null &&
    header.clientProfileUserId !== '' && (
      <Button
        label={t('reconFlags.drawer.openClientProfile')}
        variant='text'
        color='primary'
        size='sm'
        component={Link}
        target='_blank'
        to={`/clients/${header.clientProfileUserId}`}
        sx={{ alignSelf: 'flex-start', mt: 0.5 }}
      />
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
      }}
    >
      <Box sx={[{ flexShrink: 0, pt: 2 }, tokens.blockHorizontalInset]}>
        <Box sx={tokens.summaryTitleRow}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
          >
            <Box sx={tokens.prioritySwatch(swatchColor)} aria-hidden />
            {header.clientLine && (
              <Typography variant='titleMedium' component='span' noWrap>
                {header.clientLine}
              </Typography>
            )}
          </Box>
          {priorityBadge}
        </Box>

        <Box sx={tokens.metadataRows}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              component='span'
              variant='labelSmall'
              sx={tokens.metadataLabel}
            >
              {t('reconFlags.drawer.summary.type')}
            </Typography>
            <Typography
              component='span'
              variant='bodySmall'
              sx={
                header.displayType
                  ? tokens.metadataTypePill
                  : { ...tokens.metadataValue }
              }
            >
              {header.displayType ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              component='span'
              variant='labelSmall'
              sx={tokens.metadataLabel}
            >
              {t('reconFlags.drawer.summary.account')}
            </Typography>
            <Typography
              component='span'
              variant='bodySmall'
              sx={tokens.metadataValue}
            >
              {header.custodianRaw ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              component='span'
              variant='labelSmall'
              sx={tokens.metadataLabel}
            >
              {t('reconFlags.drawer.summary.firstSeen')}
            </Typography>
            <Typography
              component='span'
              variant='bodySmall'
              sx={tokens.metadataValue}
            >
              {header.createdAtDisplay ?? '—'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={tokens.sectionDivider} />

      <Tabs
        value={tab}
        variant='fullWidth'
        onChange={(_e, v: DetailTab) => setTab(v)}
        sx={tokens.tabsRoot}
      >
        <Tab value='overview' label={t('reconFlags.drawer.tabs.overview')} />
        <Tab value='notes' label={t('reconFlags.drawer.tabs.notes')} />
        <Tab value='raw' label={t('reconFlags.drawer.tabs.rawJson')} />
      </Tabs>

      {tab === 'notes' ? (
        <Box sx={tokens.notesTabPane}>
          <Box sx={tokens.notesTabHistory}>
            <Box
              sx={[tokens.blockHorizontalInset, { boxSizing: 'border-box' }]}
            >
              <ReconFlagsDrawerNotesHistory sys={sys} flag={flag} t={t} />
            </Box>
          </Box>
          <Box sx={tokens.notesTabCompose}>
            <Box
              sx={[tokens.blockHorizontalInset, { boxSizing: 'border-box' }]}
            >
              <ReconFlagsDrawerNotes
                flag={flag}
                onSaved={onNotesSaved}
                notesDraft={notesDraft}
                onNotesDraftChange={onNotesDraftChange}
                hideHeading
                textFieldDisabled={notesDisabled}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={tokens.scrollRegion}>
          <Box
            sx={[
              tokens.blockHorizontalInset,
              { pt: 2, pb: 1, boxSizing: 'border-box' },
            ]}
          >
            {tab === 'overview' &&
              overviewSections.map((section, sectionIndex) => {
                const showSectionDivider = sectionIndex > 0;
                const isLastSection =
                  sectionIndex === overviewSections.length - 1;
                const sectionOuterSx = { mb: isLastSection ? 2.5 : 0 };

                if (section.id === 'issue-summary' && section.prose) {
                  return (
                    <Fragment key={section.id}>
                      {showSectionDivider ? (
                        <Box
                          role='separator'
                          aria-hidden
                          sx={tokens.overviewSectionDivider}
                        />
                      ) : null}
                      <Box sx={sectionOuterSx}>
                        <Box sx={tokens.issueCalloutRoot}>
                          <Box sx={tokens.issueCalloutTitleRow}>
                            <ReportProblemOutlined
                              sx={tokens.issueCalloutIcon}
                              aria-hidden
                            />
                            <Typography
                              variant='bodyMedium'
                              sx={tokens.issueCalloutTitle}
                            >
                              {section.heading}
                            </Typography>
                          </Box>
                          <Typography
                            variant='bodySmall'
                            sx={tokens.issueCalloutBody}
                          >
                            {section.prose}
                          </Typography>
                        </Box>
                      </Box>
                    </Fragment>
                  );
                }

                return (
                  <Fragment key={section.id}>
                    {showSectionDivider ? (
                      <Box
                        role='separator'
                        aria-hidden
                        sx={tokens.overviewSectionDivider}
                      />
                    ) : null}
                    <Box sx={sectionOuterSx}>
                      <Typography
                        variant='titleMedium'
                        sx={tokens.sectionHeading}
                      >
                        {section.heading}
                      </Typography>
                      {section.prose != null &&
                        section.prose !== '' &&
                        section.id !== 'issue-summary' && (
                          <Typography
                            variant='bodySmall'
                            sx={{ mb: 1, whiteSpace: 'pre-wrap' }}
                          >
                            {section.prose}
                          </Typography>
                        )}
                      {section.rows.map((row) => (
                        <OverviewRow
                          key={`${section.id}|${row.label}|${row.value}`}
                          sys={sys}
                          tokens={tokens}
                          row={row}
                        />
                      ))}
                      {section.id === 'account' ? clientProfileLink : null}
                    </Box>
                  </Fragment>
                );
              })}

            {tab === 'raw' && (
              <Box component='pre' sx={tokens.rawJsonPre}>
                {JSON.stringify(flag.details ?? {}, null, 2)}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
